import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CircleInfoIcon, HandKeyIcon, PostpaidTabIcon, PrepaidTabIcon, UserKeyIcon } from "../../../../../assets/icons";
import { HelpModal } from "../../../../../common/components/help-modal/HelpModal";
import { IconRadioGroup, IconRadioOption } from "../../../../../common/components/icon-radio-group/IconRadioGroup";
import { IconTabs } from "../../../../../common/components/icon-tabs/IconTabs";
import { VerifyMeterNumberField, VerifyMeterNumberFieldHandle } from "../../../../../common/components/verify-meter-number/VerifyMeterNumberField";
import { ConsumerType, CustomerEnergyAccountRequest } from "../../../../../model/energy-profile";
import { useCreateEnergyAccountMutation, useSaveCustomerEnergyAccountAsDraftMutation } from "../../../../../service/vending-service";
import { RootState } from "../../../../../stores/store";
import { setEnergyProfileInfo } from "../../../../../stores/slices/customer-energy-profile-slice";
import { ApplicationRoutes } from "../../../../../routes";
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { trackEnergyProfileSet } from "../../../../../services/business-analytics/energyManagementEvents";

enum EnergyProfileStep {
	MeterVerification = "meterVerification",
	ResidencySelection = "residencySelection",
}

/** In sign-up the FormWrapper provides the page chrome, so render bare. */
const EnergyProfileEditLayout = ({ embedded, children }: { embedded: boolean; children: ReactNode }) => {
	const { t } = useTranslation();
	return embedded ? (
		<div>{children}</div>
	) : (
		<CustomerPageLayout title={t("common.labels.energyProfile")} showBackButton={true} contentMaxWidth={600} decreaseMainContentPadding>
			{children}
		</CustomerPageLayout>
	);
};

export interface EnergyProfileEditPageNinState {
	meterNumber: string;
	isLandlord: boolean;
	consumerType: ConsumerType | null;
}

interface EnergyProfileCreatePageProps {
	/** When embedded inside the sign-up flow: render bare (no page layout) and
	 * call `onProceedToNin` instead of navigating to the NIN route. */
	embedded?: boolean;
	onProceedToNin?: (state: EnergyProfileEditPageNinState) => void;
	/** Embedded only: invoked after a successful "Save for Later" draft so the
	 * sign-up flow can move on (profile stays not-yet-valid). */
	onSkip?: () => void;
}

export const EnergyProfileCreatePage = ({ embedded = false, onProceedToNin, onSkip }: EnergyProfileCreatePageProps = {}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const storedProfile = useSelector((state: RootState) => state.customerEnergyProfile);

	const [selectedTab, setSelectedTab] = useState<ConsumerType | null>(null);
	const [meterNumber, setMeterNumber] = useState<string>("");
	const [currentStep, setCurrentStep] = useState<EnergyProfileStep>(EnergyProfileStep.MeterVerification);
	const [hasVerificationFailed, setHasVerificationFailed] = useState(false);
	const [selectedResidencyIndex, setSelectedResidencyIndex] = useState<number | null>(null);
	const [infoModalOpen, setInfoModalOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [preferRemoteTokenLoad, setPreferRemoteTokenLoad] = useState(false);

	const prepaidMeterRef = useRef<VerifyMeterNumberFieldHandle>(null);
	const postpaidMeterRef = useRef<VerifyMeterNumberFieldHandle>(null);

	const [createEnergyAccount] = useCreateEnergyAccountMutation();
	const [saveCustomerEnergyAccountAsDraft] = useSaveCustomerEnergyAccountAsDraftMutation();

	const utilityId = process.env.REACT_APP_UTILITY_ID || storedProfile.utilityId || "";

	const residencyOptions: IconRadioOption[] = [
		{ title: t("common.labels.tenant"), subtitle: t("energyManagement.energyProfile.tenantResidencyDescription"), icon: <UserKeyIcon /> },
		{ title: t("common.labels.landlord"), subtitle: t("energyManagement.energyProfile.landlordResidencyDescription"), icon: <HandKeyIcon /> },
	];

	const getSelectedTabIndex = (): number | null => {
		if (selectedTab === ConsumerType.Prepaid) return 0;
		if (selectedTab === ConsumerType.Postpaid) return 1;
		return null;
	};

	const changeTab = (type: ConsumerType) => {
		if (type === selectedTab) return;
		setSelectedTab(type);
		setMeterNumber("");
		setCurrentStep(EnergyProfileStep.MeterVerification);
		setHasVerificationFailed(false);
		setSelectedResidencyIndex(null);
	};

	const handleVerificationResult = (result: unknown) => {
		if (result) {
			setHasVerificationFailed(false);
			setCurrentStep(EnergyProfileStep.ResidencySelection);
		} else {
			if (currentStep === EnergyProfileStep.ResidencySelection) {
				setCurrentStep(EnergyProfileStep.MeterVerification);
				setSelectedResidencyIndex(null);
			}
			setHasVerificationFailed(meterNumber.length > 0);
		}
	};

	const buildCustomerEnergyAccountRequest = (): CustomerEnergyAccountRequest => ({
		meterNumber: selectedTab === ConsumerType.Prepaid ? meterNumber : null,
		accountNumber: selectedTab === ConsumerType.Postpaid ? meterNumber : null,
		utilityId,
		consumerType: selectedTab != null ? ConsumerType[selectedTab] : undefined,
		residencyStatus: selectedResidencyIndex === 0 ? "Tenant" : selectedResidencyIndex === 1 ? "Landlord" : undefined,
		preferRemoteTokenLoad,
	});

	const saveAsDraft = async () => {
		if (!selectedTab || !meterNumber) return;

		setIsSaving(true);
		try {
			const request = buildCustomerEnergyAccountRequest();
			await saveCustomerEnergyAccountAsDraft(request).unwrap();
			toast.success(t("account.web.savedAsDraft"));
			if (embedded) onSkip?.();
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) || t("account.web.failedToSave"));
		} finally {
			setIsSaving(false);
		}
	};

	const navigateToNin = () => {
		const ninState: EnergyProfileEditPageNinState = {
			meterNumber,
			isLandlord: selectedResidencyIndex === 1,
			consumerType: selectedTab,
		};
		if (onProceedToNin) {
			onProceedToNin(ninState);
			return;
		}
		navigate(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.ENERGY_PROFILE_NIN}`, { state: ninState });
	};

	const submitForm = async () => {
		setIsSaving(true);
		try {
			const request = buildCustomerEnergyAccountRequest();
			const created = await createEnergyAccount(request).unwrap();
			trackEnergyProfileSet("success");
			if (created) {
				dispatch(setEnergyProfileInfo(created));
			}
			navigateToNin();
		} catch (err: any) {
			trackEnergyProfileSet("failed");
			toast.error(getErrorMessage(err?.data) || t("account.web.failedToSaveEnergyProfile"));
		} finally {
			setIsSaving(false);
		}
	};

	const triggerVerify = async () => {
		setIsSaving(true);
		const ref = selectedTab === ConsumerType.Prepaid ? prepaidMeterRef : postpaidMeterRef;
		await ref.current?.verify();
		setIsSaving(false);
	};

	const submitButtonPressed = async () => {
		if (currentStep === EnergyProfileStep.MeterVerification) {
			await triggerVerify();
		} else if (currentStep === EnergyProfileStep.ResidencySelection) {
			if (selectedResidencyIndex !== null) submitForm();
		}
	};

	const isSubmitDisabled =
		isSaving ||
		!selectedTab ||
		(currentStep === EnergyProfileStep.MeterVerification && !meterNumber) ||
		(currentStep === EnergyProfileStep.ResidencySelection && selectedResidencyIndex === null);

	const showResidencySection = currentStep === EnergyProfileStep.ResidencySelection;

	return (
		<>
			<EnergyProfileEditLayout embedded={embedded}>
				{/* Step 1: Select Service Type */}
				<IconTabs
					tabs={[
						{ title: t("common.labels.prepaid"), icon: <PrepaidTabIcon />, onTap: () => changeTab(ConsumerType.Prepaid) },
						{ title: t("common.labels.postpaid"), icon: <PostpaidTabIcon />, onTap: () => changeTab(ConsumerType.Postpaid) },
					]}
					selectedIndex={getSelectedTabIndex()}
					headerSectionTitle={t("energyManagement.energyProfile.selectServiceType")}
					headerSectionRightContent={
						<div onClick={() => setInfoModalOpen(true)} style={{ cursor: "pointer" }}>
							<CircleInfoIcon />
						</div>
					}
					iconSize={24}
					circleSize={40}
				/>

				<div style={{ height: 8 }} />

				{/* Prepaid content */}
				{selectedTab === ConsumerType.Prepaid && (
					<>
						<VerifyMeterNumberField
							ref={prepaidMeterRef}
							value={meterNumber}
							onChange={setMeterNumber}
							consumerType={ConsumerType.Prepaid}
							title={t("common.labels.meterNumber")}
							placeholder={t("common.labels.meterNumber")}
							utilityId={utilityId}
							onVerificationResult={handleVerificationResult}
							hideVerifyButton
							headerSectionRightContent={
								<div onClick={() => setInfoModalOpen(true)} style={{ cursor: "pointer" }}>
									<CircleInfoIcon />
								</div>
							}
						/>

						{showResidencySection && (
							<>
								<div style={{ height: 8 }} />
								<IconRadioGroup
									options={residencyOptions}
									selectedIndex={selectedResidencyIndex}
									onOptionSelected={setSelectedResidencyIndex}
									headerTitle={t("energyManagement.energyProfile.selectResidencyStatus")}
									headerRightContent={
										<div onClick={() => setInfoModalOpen(true)} style={{ cursor: "pointer" }}>
											<CircleInfoIcon />
										</div>
									}
									iconSize={24}
								/>
							</>
						)}
					</>
				)}

				{/* Postpaid content */}
				{selectedTab === ConsumerType.Postpaid && (
					<>
						<VerifyMeterNumberField
							ref={postpaidMeterRef}
							value={meterNumber}
							onChange={setMeterNumber}
							consumerType={ConsumerType.Postpaid}
							utilityId={utilityId}
							title={t("common.labels.accountNumber")}
							placeholder={t("common.labels.accountNumber")}
							onVerificationResult={handleVerificationResult}
							hideVerifyButton
						/>

						{showResidencySection && (
							<>
								<div style={{ height: 8 }} />
								<IconRadioGroup
									options={residencyOptions}
									selectedIndex={selectedResidencyIndex}
									onOptionSelected={setSelectedResidencyIndex}
									headerTitle={t("energyManagement.energyProfile.selectResidencyStatus")}
									headerRightContent={
										<div onClick={() => setInfoModalOpen(true)} style={{ cursor: "pointer" }}>
											<CircleInfoIcon />
										</div>
									}
									iconSize={24}
								/>
							</>
						)}
					</>
				)}

				<div style={{ height: 16 }} />

				{/* Bottom action buttons */}
				<div style={{ display: "flex", gap: 12, width: "100%" }}>
					{hasVerificationFailed && currentStep === EnergyProfileStep.MeterVerification ? (
						<>
							<button className="app-secondary-btn" onClick={saveAsDraft} disabled={isSaving} style={{ flex: 1, width: "auto" }}>
								{t("energyManagement.saveForLater")}
							</button>
							<button className="app-primary-btn" onClick={triggerVerify} disabled={isSaving} style={{ flex: 1, width: "auto" }}>
								{t("energyManagement.retry")}
							</button>
						</>
					) : (
						<button className="app-primary-btn" onClick={submitButtonPressed} disabled={isSubmitDisabled} style={{ flex: 1, width: "auto" }}>
							{t("common.actions.continueKey")}
						</button>
					)}
				</div>
			</EnergyProfileEditLayout>

			<HelpModal
				isOpen={infoModalOpen}
				onClose={() => setInfoModalOpen(false)}
				title={t("common.labels.signinHelpTitle")}
				description={t("common.labels.signinHelpDescription")}
			/>
		</>
	);
};
